<template>
  <section id="how" class="rk-how">
    <div class="rk-container">     
      <div class="rk-section__head">
        <div class="rk-kicker">
          <span class="rk-kicker__rule"></span>
          <span class="rk-kicker__text"><i class="bi bi-signpost-split"></i> How it works</span>
        </div>
        <h2>From spreadsheet to system, in three steps</h2>
      </div>

      <div class="rk-how__track">
        <svg class="rk-how__line" viewBox="0 0 1000 2" preserveAspectRatio="none">
          <line
            x1="0" y1="1" x2="1000" y2="1"
            stroke="var(--brass)" stroke-width="2" stroke-dasharray="1000"
            :stroke-dashoffset="lineOffset"
            style="transition: stroke-dashoffset 1.2s var(--ease)"
          />
        </svg>

        <div class="rk-how__steps" ref="stepsEl">
          <div class="rk-how__step" v-reveal="{ delay: 0 }" v-tilt>
            <span class="rk-how__num">01</span>
            <div class="rk-how__icon"><i class="bi bi-buildings"></i></div>
            <h3>Add your properties &amp; tenants</h3>
            <p>Bring in units, tenants, and lease details — bulk import or add them one at a time as you onboard.</p>
          </div>

          <div class="rk-how__step" v-reveal="{ delay: 150 }" v-tilt>
            <span class="rk-how__num">02</span>
            <div class="rk-how__icon"><i class="bi bi-gear-wide-connected"></i></div>
            <h3>Automate rent &amp; communication</h3>
            <p>Monthly billing, SMS reminders, and payment receipts run on their own — no more manual follow-ups.</p>
          </div>

          <div class="rk-how__step" v-reveal="{ delay: 300 }" v-tilt>
            <span class="rk-how__num">03</span>
            <div class="rk-how__icon"><i class="bi bi-graph-up"></i></div>
            <h3>Track everything in real time</h3>
            <p>See collection rates, vacancies, and performance the moment they change, from any device.</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'

const stepsEl = ref(null)
const lineOffset = ref(1000)
let observer

onMounted(() => {
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          lineOffset.value = 0
          observer.disconnect()
        }
      })
    },
    { threshold: 0.4 }
  )
  if (stepsEl.value) observer.observe(stepsEl.value)
})
onUnmounted(() => observer?.disconnect())
</script>

<style scoped>
.rk-container { max-width: 1200px; margin: 0 auto; padding: 0 1.5rem; }
.rk-how { padding: 6.5rem 0; background: var(--paper); }

.rk-kicker {
  display: inline-flex; align-items: center; gap: 0.85rem;
  margin-bottom: 1rem;
}
.rk-kicker__rule { width: 32px; height: 3px; background: var(--brass); border-radius: 2px; flex-shrink: 0; }
.rk-kicker__text {
  display: inline-flex; align-items: center; gap: 0.5rem;
  font-family: var(--font-mono); font-size: 0.95rem; font-weight: 700;
  letter-spacing: 0.12em; text-transform: uppercase; color: var(--brass);
}
.rk-kicker__text i { font-size: 1rem; }

.rk-section__head h2 {
  font-family: var(--font-display);
  font-weight: 400;
  font-size: 2.6rem;
  letter-spacing: -0.01em;
  color: var(--ink);
  line-height: 1.15;
}

.rk-section__head {
  max-width: 640px;
  margin-bottom: 80px;
}



.rk-how__track { position: relative; }
.rk-how__line { position: absolute; top: 76px; left: 8%; width: 84%; height: 2px; display: block; }

.rk-how__steps { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2rem; position: relative; }

.rk-how__step {
  background: var(--neu-surface); border-radius: var(--radius-lg);
  box-shadow: var(--neu-raised); padding: 2.25rem 2rem; text-align: center;
  position: relative;
}

.rk-how__num {
  position: absolute; top: 1.25rem; right: 1.5rem;
  font-family: var(--font-mono); font-size: 0.8rem; color: var(--slate-soft);
}

.rk-how__icon {
  width: 60px; height: 60px; margin: 0 auto 1.5rem;
  border-radius: 50%; background: var(--neu-surface); box-shadow: var(--neu-raised-sm);
  display: flex; align-items: center; justify-content: center;
  color: var(--brass); font-size: 1.6rem;
}

.rk-how__step h3 { font-family: var(--font-body); font-weight: 700; font-size: 1.2rem; color: var(--ink); margin: 0 0 0.75rem; }
.rk-how__step p { font-family: var(--font-body); font-size: 0.92rem; color: var(--slate); line-height: 1.6; margin: 0; }

@media (max-width: 860px) {
  .rk-how__line { display: none; }
  .rk-how__steps { grid-template-columns: 1fr; gap: 1.5rem; }
}
@media (max-width: 560px) {
  .rk-how { padding: 4.5rem 0; }
  .rk-how__head h2 { font-size: 1.9rem; }
}
</style>